import React, { createContext, useState, ReactNode, useContext, useEffect } from 'react';
import { tokenService } from '../services/tokenService';
import axiosInstance from '../api/axiosInstance';

export interface User {
  id: number;
  email: string;
  name?: string;
  surname?: string;
  username?: string;
  organization?: string;
  role?: string;
  client_id?: number;
  broker_id?: number | null;
  is_super_admin?: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (token: string, user: User) => void;
  logout: () => void;
  setUser: (user: User | null) => void;
  refreshUser: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Check for existing session on mount
  useEffect(() => {
    const initAuth = async () => {
  console.log('🔍 AuthProvider initializing...');
  console.log('🔍 localStorage token:', localStorage.getItem('token'));
  console.log('🔍 sessionStorage token:', sessionStorage.getItem('token'));
  
  const savedToken = tokenService.getToken();
  const savedUser = localStorage.getItem('user');
  
  console.log('🔍 Saved token found:', savedToken ? 'Yes' : 'No');
  console.log('🔍 Saved user found:', savedUser ? 'Yes' : 'No');
  
  if (savedToken) {
    console.log('🔍 Token value:', savedToken.substring(0, 30) + '...');
    setToken(savedToken);
    
    if (savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        console.log('👤 User data from localStorage:', userData);
        setUser(userData);
        setIsAuthenticated(true);
        console.log('✅ AuthProvider restored session from localStorage');
      } catch (error) {
        console.error('❌ Error parsing saved user:', error);
        localStorage.removeItem('user');
        tokenService.clearToken();
        setIsAuthenticated(false);
      }
    } else {
      // Token exists but no user - try to fetch user
      console.log('📤 Token exists but no user, fetching user...');
      try {
        await refreshUser();
      } catch (error) {
        console.error('❌ Failed to refresh user on init:', error);
        tokenService.clearToken();
        setIsAuthenticated(false);
      }
    }
  } else {
    console.log('ℹ️ No saved session found');
    setIsAuthenticated(false);
  }
  
  setIsLoading(false);
  console.log('🔍 AuthProvider init complete - isAuthenticated:', isAuthenticated);
};
    
    initAuth();
  }, []);

  const refreshUser = async () => {
    try {
      console.log('🔄 Refreshing user data...');
      const response = await axiosInstance.get('/auth/me');
      const userData = response.data;
      
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      setIsAuthenticated(true);
      console.log('✅ User refreshed successfully');
      return userData;
    } catch (error) {
      console.error('❌ Failed to refresh user:', error);
      throw error;
    }
  };

  const login = (newToken: string, userData: User) => {
    console.log('📝 AuthProvider.login called');
    
    // Store token using tokenService
    tokenService.setToken(newToken);
    setToken(newToken);
    
    // Store user
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
    
    setIsAuthenticated(true);
    console.log('✅ Login successful, user set:', userData);
  };

  const logout = () => {
    console.log('🗑️ AuthProvider.logout called');
    
    // Clear token using tokenService
    tokenService.clearToken();
    setToken(null);
    
    // Clear user
    setUser(null);
    localStorage.removeItem('user');
    
    setIsAuthenticated(false);
    console.log('✅ Logout successful');
  };

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated,
    isLoading,
    login,
    logout,
    setUser,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};